import { createHashHistory, createRootRoute, createRoute, createRouter, lazyRouteComponent, Link, Outlet, redirect, type RouteComponent } from "@tanstack/react-router";
import type { MeDTO } from "@shared/types";
import { can, type Permission } from "@shared/domain";
import { api, ApiError } from "@/lib/api";

import { queryClient } from "@/lib/queryClient";
import { useSession } from "@/stores/session";
import { AppShell } from "@/components/shell/AppShell";
import { ConfirmHost, ToastHost } from "@/components/overlays";
import { EmptyState } from "@/components/ui";
import { AcceptInvitePage, AuthLayout, ChangePasswordPage, ForgotPage, ResetPage, SignInPage, TwoFactorPage } from "@/features/auth/AuthPages";
import { endpoints } from "@/lib/endpoints";
import { queryKeys } from "@/lib/queryKeys";
import { staffRoutes } from "@/features/staff/routes";

type Search = { booking?: string };

const rootRoute = createRootRoute({
  validateSearch: (s: Record<string, unknown>): Search => (typeof s.booking === "string" && s.booking ? { booking: s.booking } : {}),
  component: () => (
    <>
      <Outlet />
      <ToastHost />
      <ConfirmHost />
    </>
  ),
  notFoundComponent: () => (
    <div className="p-8">
      <EmptyState title="Nothing lives at this address" body="The page may have moved. Head back to Today." action={
          <Link to="/" className="text-deodar underline">
            Today
          </Link>
        } />
    </div>
  ),
});

/* ── Signed-out screens ── */

const authLayout = createRoute({ getParentRoute: () => rootRoute, id: "auth", component: AuthLayout });
const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);

const signIn = createRoute({
  getParentRoute: () => authLayout,
  path: "/sign-in",
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => ({ redirect: str(s.redirect) }),
  component: SignInPage,
});
const twoFactor = createRoute({
  getParentRoute: () => authLayout,
  path: "/two-factor",
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => ({ redirect: str(s.redirect) }),
  component: TwoFactorPage,
});
const forgot = createRoute({ getParentRoute: () => authLayout, path: "/forgot-password", component: ForgotPage });
const reset = createRoute({
  getParentRoute: () => authLayout,
  path: "/reset-password",
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({ token: str(s.token) }),
  component: ResetPage,
});
const acceptInvite = createRoute({
  getParentRoute: () => authLayout,
  path: "/accept-invite",
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({ token: str(s.token) }),
  component: AcceptInvitePage,
});
const changePassword = createRoute({ getParentRoute: () => authLayout, path: "/change-password", component: ChangePasswordPage });

/* ── Signed-in shell ── */

async function loadMe(): Promise<MeDTO> {
  return queryClient.ensureQueryData({ queryKey: queryKeys.me.all, queryFn: () => api<MeDTO>(endpoints.auth.me, { quiet401: true }) });
}

const appLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ location }) => {
    try {
      const me = await loadMe();
      if (me.mustChangePassword) throw redirect({ to: "/change-password" });
      if (me.email) useSession.getState().remember(me.email, me.name);
      return { me };
    } catch (e) {
      if (e instanceof ApiError) {
        // Staff only ever use the staff app.
        if (e.code === "staff_app") throw redirect({ to: "/staff" });
        if (e.code === "mfa_required") throw redirect({ to: "/two-factor", search: { redirect: location.href } });
        if (e.code === "password_change_required") throw redirect({ to: "/change-password" });
        if (e.status === 401) throw redirect({ to: "/sign-in", search: { redirect: location.href === "/" ? undefined : location.href } });
      }
      throw e;
    }
  },
  component: AppShell,
});

function gate(permission: Permission) {
  return () => {
    const me = queryClient.getQueryData<MeDTO>(queryKeys.me.all);
    if (!me || !can(me, permission)) throw redirect({ to: "/" });
  };
}

/** Exactly these roles, whatever their permissions (reports are the owner's). */
function gateRoles(...roles: MeDTO["role"][]) {
  return () => {
    const me = queryClient.getQueryData<MeDTO>(queryKeys.me.all);
    if (!me || !roles.includes(me.role)) throw redirect({ to: "/" });
  };
}

const page = <T extends Record<string, unknown>, K extends keyof T & string>(loader: () => Promise<T>, name: K) => lazyRouteComponent(loader, name);

const today = createRoute({ getParentRoute: () => appLayout, path: "/", component: page(() => import("@/features/today/TodayPage"), "TodayPage") });
const newBooking = createRoute({
  getParentRoute: () => appLayout,
  path: "/new",
  validateSearch: (
    s: Record<string, unknown>,
  ): { propertyId?: string; roomId?: string; bedId?: string; checkIn?: string; checkOut?: string; adults?: number; step?: number } => ({
    propertyId: str(s.propertyId),
    roomId: str(s.roomId),
    bedId: str(s.bedId),
    checkIn: str(s.checkIn),
    checkOut: str(s.checkOut),
    adults: Number(s.adults) > 0 ? Number(s.adults) : undefined,
    step: Number(s.step) >= 1 && Number(s.step) <= 6 ? Number(s.step) : undefined,
  }),
  component: page(() => import("@/features/booking/NewBookingPage"), "NewBookingPage"),
});

/** A bare area address lands on its first section; the nav itself links to the remembered one. */
function firstSection(to: string) {
  return () => {
    throw redirect({ to: to as "/", replace: true });
  };
}

/* 02 Front desk: calendar, conflicts, guests. */
const desk = createRoute({ getParentRoute: () => appLayout, path: "/desk", beforeLoad: firstSection("/desk/calendar") });
const deskCalendar = createRoute({
  getParentRoute: () => appLayout,
  path: "/desk/calendar",
  component: page(() => import("@/features/desk/calendar/CalendarPage"), "CalendarPage"),
});
const deskConflicts = createRoute({
  getParentRoute: () => appLayout,
  path: "/desk/conflicts",
  component: page(() => import("@/features/desk/conflicts/ConflictsPage"), "ConflictsPage"),
});
const deskGuests = createRoute({ getParentRoute: () => appLayout, path: "/desk/guests", component: page(() => import("@/features/desk/guests/GuestsPage"), "GuestsPage") });

/*
 * 03 Room checks: Plan · Review · Checklists · Rules for managers (tabs in the
 * page), My checks for staff. Running a check is the whole screen on a phone.
 */
const checksModule = () => import("@/features/checks");
export type PlanSearch = { day?: "tomorrow" };
const checks = createRoute({
  getParentRoute: () => appLayout,
  path: "/checks",
  validateSearch: (s: Record<string, unknown>): PlanSearch => (s.day === "tomorrow" ? { day: "tomorrow" } : {}),
  component: page(checksModule, "ChecksHome"),
});
const checksReview = createRoute({ getParentRoute: () => appLayout, path: "/checks/review", beforeLoad: gate("upkeep.assign"), component: page(checksModule, "ReviewPage") });
export type ChecklistSearch = { stage?: "ready" | "pre" | "checkout"; room?: string };
const checklistSearch = (s: Record<string, unknown>): ChecklistSearch => ({
  stage: s.stage === "ready" || s.stage === "pre" || s.stage === "checkout" ? s.stage : undefined,
  room: str(s.room),
});
/** The bare address picks the first room type once the list has loaded. */
const checksChecklists = createRoute({
  getParentRoute: () => appLayout,
  path: "/checks/checklists",
  beforeLoad: gate("upkeep.setup"),
  validateSearch: checklistSearch,
  component: page(checksModule, "ChecklistsPage"),
});
const checksChecklist = createRoute({
  getParentRoute: () => appLayout,
  path: "/checks/checklists/$roomTypeId",
  beforeLoad: gate("upkeep.setup"),
  validateSearch: checklistSearch,
  component: page(checksModule, "ChecklistsPage"),
});
const checksRules = createRoute({ getParentRoute: () => appLayout, path: "/checks/rules", beforeLoad: gate("upkeep.setup"), component: page(checksModule, "RulesPage") });
const checksRoom = createRoute({
  getParentRoute: () => appLayout,
  path: "/checks/rooms/$roomId",
  validateSearch: (s: Record<string, unknown>): { tab?: "today" | "history" | "repairs" } => ({
    tab: s.tab === "history" || s.tab === "repairs" ? s.tab : undefined,
  }),
  component: page(checksModule, "RoomPage"),
});
const checksRun = createRoute({ getParentRoute: () => appLayout, path: "/checks/tasks/$taskId/run", component: page(checksModule, "RunCheckPage") });

/* 04 Repairs: the board of what's broken. ?issue=<id> opens one over it — from Today, or a shared link. */
const repairs = createRoute({
  getParentRoute: () => appLayout,
  path: "/repairs",
  validateSearch: (s: Record<string, unknown>): { issue?: string } => ({ issue: str(s.issue) }),
  component: page(() => import("@/features/repairs/IssuesPage"), "IssuesPage"),
});

/* 05 Laundry: Stock, Batches, Vendors & bill (its own tabs). */
const laundryModule = () => import("@/features/laundry");
const laundry = createRoute({ getParentRoute: () => appLayout, path: "/laundry", beforeLoad: firstSection("/laundry/stock") });
const laundryStock = createRoute({ getParentRoute: () => appLayout, path: "/laundry/stock", component: page(laundryModule, "LaundryStockPage") });
const laundryBatches = createRoute({ getParentRoute: () => appLayout, path: "/laundry/batches", component: page(laundryModule, "LaundryBatchesPage") });
const laundryBatch = createRoute({ getParentRoute: () => appLayout, path: "/laundry/batches/$batchId", component: page(laundryModule, "ReceiveBatchPage") });
const laundryVendors = createRoute({ getParentRoute: () => appLayout, path: "/laundry/vendors", beforeLoad: gate("money.view"), component: page(laundryModule, "LaundryVendorsPage") });
const laundryVendorNew = createRoute({ getParentRoute: () => appLayout, path: "/laundry/vendors/new", beforeLoad: gate("upkeep.setup"), component: page(laundryModule, "LaundryVendorsPage") });
const laundryVendor = createRoute({ getParentRoute: () => appLayout, path: "/laundry/vendors/$vendorId", beforeLoad: gate("upkeep.setup"), component: page(laundryModule, "LaundryVendorsPage") });

/* Lost & found belongs to Today. ?item=<id> opens an item, ?log=1 the form. */
export type LostSearch = { item?: string; log?: 1 };
const lost = createRoute({
  getParentRoute: () => appLayout,
  path: "/lost",
  validateSearch: (s: Record<string, unknown>): LostSearch => ({ item: str(s.item), log: s.log === 1 || s.log === "1" ? 1 : undefined }),
  component: page(() => import("@/features/today/lost/LostPage"), "LostPage"),
});

/* 06 Transactions: rates (managers) and spend (everyone). */
const transactions = createRoute({
  getParentRoute: () => appLayout,
  path: "/transactions",
  beforeLoad: () => {
    const me = queryClient.getQueryData<MeDTO>(queryKeys.me.all);
    return firstSection(me && can(me, "rates.manage") ? "/transactions/rates" : "/transactions/spend")();
  },
});
const rates = createRoute({
  getParentRoute: () => appLayout,
  path: "/transactions/rates",
  beforeLoad: gate("rates.manage"),
  component: page(() => import("@/features/transactions/rates/RatesPage"), "RatesPage"),
});
const spend = createRoute({ getParentRoute: () => appLayout, path: "/transactions/spend", component: page(() => import("@/features/transactions/spend/SpendPage"), "SpendPage") });

/* 07 Reports: an index, and each report on its own page. Owner only. */
const reportsModule = () => import("@/features/reports/ReportsPage");
/** Reports opens on today's overview; the rail holds the full reports. */
const reports = createRoute({ getParentRoute: () => appLayout, path: "/reports", beforeLoad: gateRoles("owner"), component: page(reportsModule, "ReportsOverviewPage") });
const performanceReport = createRoute({
  getParentRoute: () => appLayout,
  path: "/reports/performance",
  beforeLoad: gateRoles("owner"),
  component: page(reportsModule, "PerformanceReportPage"),
});
const sourcesReport = createRoute({
  getParentRoute: () => appLayout,
  path: "/reports/sources",
  beforeLoad: gateRoles("owner"),
  component: page(reportsModule, "SourcesReportPage"),
});
const cancellationsReport = createRoute({
  getParentRoute: () => appLayout,
  path: "/reports/cancellations",
  beforeLoad: gateRoles("owner"),
  component: page(reportsModule, "CancellationsReportPage"),
});

/* Settings: one section at a time, opened from the account menu. /settings alone is the phone's section list. */
const settings = createRoute({
  getParentRoute: () => appLayout,
  path: "/settings",
  component: page(() => import("@/features/settings/SettingsLayout"), "SettingsLayout"),
});
const section = <P extends string>(path: P, component: RouteComponent, beforeLoad?: () => void) =>
  createRoute({ getParentRoute: () => settings, path, beforeLoad, component });
const settingsList = section("/", () => null);
const settingsGeneral = section("general", page(() => import("@/features/settings/general/StayRules"), "StayRulesSection"));
const settingsMessages = section("messages", page(() => import("@/features/settings/messages/GuestMessages"), "GuestMessagesSection"), gate("settings.manage"));
const settingsRooms = section("rooms", page(() => import("@/features/settings/rooms/PropertiesPage"), "RoomsSection"));
const settingsAccess = createRoute({
  getParentRoute: () => settings,
  path: "access",
  beforeLoad: gate("access.rotate"),
  /** ?room=B4 preselects a room — from the room page, or a checkout's "change the code?" nudge. */
  validateSearch: (s: Record<string, unknown>): { room?: string } => ({ room: str(s.room) }),
  component: page(() => import("@/features/settings/access/AccessPage"), "AccessSection"),
});
const settingsTaxes = section("taxes", page(() => import("@/features/settings/taxes/TaxesPanel"), "TaxesSection"), gate("settings.manage"));
const settingsExtras = section("extras", page(() => import("@/features/settings/extras/ExtrasPage"), "ExtrasSection"), gate("rates.manage"));
const settingsPayments = section("payments", page(() => import("@/features/settings/payments/PaymentMethodsPage"), "PaymentMethodsSection"), gate("money.view"));
const settingsSources = section("sources", page(() => import("@/features/settings/sources/ChannelsPanel"), "ChannelsPanel"), gate("settings.manage"));
const settingsChannel = section("channel", page(() => import("@/features/settings/channel/ChannelManagerPanel"), "ChannelManagerSection"), gate("settings.manage"));
const settingsUsers = section("users", page(() => import("@/features/settings/users/UsersPage"), "UsersSection"), gate("users.manage"));
const settingsActivity = section("activity", page(() => import("@/features/settings/activity/ActivityPage"), "ActivitySection"));
const settingsSecurity = section("security", page(() => import("@/features/settings/security/SecurityPage"), "SecuritySection"));
const settingsDisplay = section("display", page(() => import("@/features/settings/display/DisplayPanel"), "DisplayPanel"));

/*
 * Addresses from before. Emails, bookmarks, the installed app's shortcuts
 * and old toasts still carry them; each forwards with its search intact
 * (?issue=, ?room=, ?booking=).
 */
function moved(path: string, to: string | ((params: Record<string, string>) => string)) {
  return createRoute({
    getParentRoute: () => appLayout,
    path,
    beforeLoad: ({ location, params }) => {
      throw redirect({ to: (typeof to === "string" ? to : to(params as Record<string, string>)) as "/", search: location.search as never, replace: true });
    },
  });
}
const oldPaths = [
  moved("/availability", "/desk/calendar"),
  moved("/conflicts", "/desk/conflicts"),
  moved("/guests", "/desk/guests"),
  moved("/issues", "/repairs"),
  // The Upkeep area, split into Room checks, Repairs, Laundry and Lost & found.
  moved("/upkeep", "/checks"),
  moved("/upkeep/board", "/checks"),
  moved("/upkeep/rooms", "/checks"),
  moved("/upkeep/review", "/checks/review"),
  moved("/upkeep/rooms/$roomId", (p) => `/checks/rooms/${p.roomId}`),
  moved("/upkeep/tasks/$taskId", (p) => `/checks/tasks/${p.taskId}/run`),
  moved("/upkeep/tasks/$taskId/run", (p) => `/checks/tasks/${p.taskId}/run`),
  moved("/upkeep/setup/checklists", "/checks/checklists"),
  moved("/upkeep/setup/checklists/$roomId", "/checks/checklists"),
  moved("/upkeep/checklists", "/checks/checklists"),
  moved("/settings/checklists", "/checks/checklists"),
  moved("/upkeep/repairs", "/repairs"),
  moved("/upkeep/laundry", "/laundry/stock"),
  moved("/upkeep/laundry/$", (p) => `/laundry/${p._splat}`),
  moved("/upkeep/lost", "/lost"),
  // Money became Transactions; Reports its own area.
  moved("/money", "/transactions"),
  moved("/money/rates", "/transactions/rates"),
  moved("/money/spend", "/transactions/spend"),
  moved("/money/reports", "/reports"),
  moved("/money/reports/$report", (p) => `/reports/${p.report}`),
  moved("/rates", "/transactions/rates"),
  moved("/spend", "/transactions/spend"),
  moved("/properties", "/settings/rooms"),
  moved("/users", "/settings/users"),
  moved("/activity", "/settings/activity"),
];

const routeTree = rootRoute.addChildren([
  authLayout.addChildren([signIn, twoFactor, forgot, reset, acceptInvite, changePassword]),
  staffRoutes(rootRoute),
  appLayout.addChildren([
    today,
    newBooking,
    desk,
    deskCalendar,
    deskConflicts,
    deskGuests,
    checks,
    checksReview,
    checksChecklists,
    checksChecklist,
    checksRules,
    checksRoom,
    checksRun,
    repairs,
    laundry,
    laundryStock,
    laundryBatches,
    laundryBatch,
    laundryVendors,
    laundryVendorNew,
    laundryVendor,
    lost,
    transactions,
    rates,
    spend,
    reports,
    performanceReport,
    sourcesReport,
    cancellationsReport,
    settings.addChildren([
      settingsList,
      settingsGeneral,
      settingsMessages,
      settingsRooms,
      settingsAccess,
      settingsTaxes,
      settingsExtras,
      settingsPayments,
      settingsSources,
      settingsChannel,
      settingsUsers,
      settingsActivity,
      settingsSecurity,
      settingsDisplay,
    ]),
    ...oldPaths,
  ]),
]);

/*
 * Hash routing: the route lives after `/#`, so the server only ever serves
 * index.html and a deep link can't 404. Links minted before the switch —
 * emails, bookmarks, the installed app's shortcuts — arrive as real paths;
 * move them behind the hash before the router reads the URL.
 */
if (window.location.pathname !== "/" && window.location.pathname !== "/index.html" && !window.location.hash) {
  window.history.replaceState(null, "", `/#${window.location.pathname}${window.location.search}`);
}

export const router = createRouter({
  routeTree,
  history: createHashHistory(),
  defaultPreload: "intent",
  defaultPendingMs: 150,
  defaultPendingComponent: () => (
    <div className="p-8">
      <div className="shimmer h-3.5 w-1/3 rounded-full" />
    </div>
  ),
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
